import React, { useState, useEffect } from 'react';
import { inr } from './portfolioHealthCheckApi';
import FundCandidateSelector from './FundCandidateSelector';

export default function HoldingsReviewList({
  review,
  busy,
  onEdit,
  onSearch,
  isClient = false,
  uiState = {},
  onUiChange,
}) {
  const [changing, setChanging] = useState(null);
  const [editingAmounts, setEditingAmounts] = useState({});

  const rows = review.rows || [];
  const groups = {
    attention: rows.filter((r) => r.bucket === 'identity_to_confirm' || r.bucket === 'value_to_confirm'),
    matched: rows.filter((r) => r.bucket === 'matched'),
    notCompared: rows.filter((r) => r.bucket === 'excluded_by_type' || r.bucket === 'left_out'),
  };

  const notComparedValue = groups.notCompared.reduce((sum, r) => sum + (r.currentValue || 0), 0);
  const totalValue = review.totals?.knownCurrentValue || 0;
  const notComparedPct = totalValue > 0 ? ((notComparedValue / totalValue) * 100).toFixed(1) : '0.0';

  // Effective UI states (either from props sync for Client or local for Advisor)
  const effectiveEditingAmounts = isClient ? (uiState.editingAmounts || {}) : editingAmounts;
  const effectiveChanging = isClient ? (uiState.changing ?? null) : changing;
  const openAccordions = uiState.openAccordions || { attention: true, notCompared: true, matched: true };

  // Keep local editing amounts in sync with review on advisor side
  useEffect(() => {
    if (isClient) return;
    const initial = {};
    groups.matched.forEach((r) => {
      if (r.currentValue != null) {
        initial[r.positionRef] = r.currentValue;
      }
    });
    setEditingAmounts(initial);
  }, [review, isClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAmountChange = (positionRef, val) => {
    if (isClient) return;
    const next = { ...editingAmounts, [positionRef]: val };
    setEditingAmounts(next);
    onUiChange?.({ editingAmounts: next });
  };

  const handleAmountBlur = (r) => {
    if (isClient) return;
    const raw = editingAmounts[r.positionRef];
    if (raw === undefined || raw === null || raw === '') return;
    const num = parseFloat(String(raw).replace(/[₹,\s]/g, ''));
    if (!isNaN(num) && num > 0 && num !== r.currentValue) {
      onEdit([{
        action: 'set_value',
        positionRef: r.positionRef,
        currentValue: num,
        valuationDate: r.valuationDate || new Date().toISOString().slice(0, 10),
      }]);
    } else {
      // Revert if invalid
      const next = { ...editingAmounts, [r.positionRef]: r.currentValue };
      setEditingAmounts(next);
      onUiChange?.({ editingAmounts: next });
    }
  };

  const handleAmountKeyDown = (e, r) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const toggleChanging = (positionRef) => {
    if (isClient) return;
    const next = changing === positionRef ? null : positionRef;
    setChanging(next);
    onUiChange?.({ changing: next });
  };

  const handleToggleAccordion = (key, isOpen) => {
    if (isClient) return;
    const next = { ...openAccordions, [key]: isOpen };
    onUiChange?.({ openAccordions: next });
  };

  return (
    <div className="phc-holdings-container">
      {/* Needs confirmation section if any */}
      {groups.attention.length > 0 && (
        <details
          className="phc-card phc-holdings-accordion phc-attention-card"
          open={openAccordions.attention !== undefined ? openAccordions.attention : true}
          onToggle={(e) => handleToggleAccordion('attention', e.target.open)}
        >
          <summary
            className="phc-holdings-summary"
            onClick={(e) => { if (isClient) e.preventDefault(); }}
            style={isClient ? { cursor: 'default' } : undefined}
          >
            ▼ Needs confirmation ({groups.attention.length})
          </summary>
          <div className="phc-holdings-body">
            <ul className="phc-list">
              {groups.attention.map((r) => (
                <li key={r.positionRef} className="phc-holding" data-bucket={r.bucket}>
                  <div className="phc-holding__main">
                    <p className="phc-holding__name">{r.matchedName || r.originalName}</p>
                    {r.matchedName && r.matchedName !== r.originalName && (
                      <p className="phc-small">As received: “{r.originalName}”</p>
                    )}
                    {r.reason && <p className="phc-small phc-holding__reason">{r.reason}</p>}
                  </div>
                  <div className="phc-holding__value">
                    <span className="phc-num">{r.currentValue != null ? inr(r.currentValue) : 'Value unknown'}</span>
                  </div>
                  {!isClient && (
                    <div className="phc-holding__actions">
                      <button
                        type="button"
                        className="phc-link"
                        disabled={busy}
                        onClick={() => toggleChanging(r.positionRef)}
                      >
                        {r.matchedName ? 'Change' : 'Choose fund'}
                      </button>
                      {r.needsTypeConfirmation && (
                        <button
                          type="button"
                          className="phc-link"
                          disabled={busy}
                          onClick={() => onEdit([{ action: 'confirm_equity', positionRef: r.positionRef }])}
                        >
                          Holds Indian shares
                        </button>
                      )}
                      <button
                        type="button"
                        className="phc-link"
                        disabled={busy}
                        onClick={() => onEdit([{ action: 'leave_out', positionRef: r.positionRef }])}
                      >
                        Leave out
                      </button>
                    </div>
                  )}
                  {effectiveChanging === r.positionRef && (
                    <FundCandidateSelector
                      row={r}
                      onSearch={onSearch}
                      busy={busy}
                      isClient={isClient}
                      onCancel={() => toggleChanging(null)}
                      onChoose={async (token) => {
                        const ok = await onEdit([{ action: 'change', positionRef: r.positionRef, candidateToken: token }]);
                        if (ok) toggleChanging(null);
                      }}
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>
        </details>
      )}

      {/* Not compared section (Image 5 style) */}
      {groups.notCompared.length > 0 && (
        <details
          className="phc-card phc-holdings-accordion"
          open={openAccordions.notCompared !== undefined ? openAccordions.notCompared : true}
          onToggle={(e) => handleToggleAccordion('notCompared', e.target.open)}
        >
          <summary
            className="phc-holdings-summary"
            onClick={(e) => { if (isClient) e.preventDefault(); }}
            style={isClient ? { cursor: 'default' } : undefined}
          >
            ▼ Not compared: {groups.notCompared.length} holding{groups.notCompared.length === 1 ? '' : 's'} ({inr(notComparedValue)}, {notComparedPct}% of your portfolio)
          </summary>
          <div className="phc-holdings-body">
            <p className="phc-not-compared-note">
              These funds were identified and stay in your portfolio total, but they do not hold company shares directly (or hedge them), so the calculator sets them aside from stock overlap automatically. They are not treated as zero overlap. No action needed.
            </p>
            <div className="phc-not-compared-list">
              {groups.notCompared.map((r) => (
                <div key={r.positionRef} className="phc-not-compared-item">
                  <span className="phc-not-compared-name">
                    {r.matchedName || r.originalName} - {r.currentValue != null ? inr(r.currentValue) : '—'}
                  </span>
                  <span className="phc-not-compared-desc">
                    {' — '}{r.reason || 'Holds bonds and money-market instruments, not company shares.'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </details>
      )}

      {/* Matched holdings section (Image 5 style) */}
      <details
        className="phc-card phc-holdings-accordion"
        open={openAccordions.matched !== undefined ? openAccordions.matched : true}
        onToggle={(e) => handleToggleAccordion('matched', e.target.open)}
      >
        <summary
          className="phc-holdings-summary"
          onClick={(e) => { if (isClient) e.preventDefault(); }}
          style={isClient ? { cursor: 'default' } : undefined}
        >
          ▼ Matched holdings ({groups.matched.length}) — review or edit
        </summary>
        <div className="phc-holdings-body">
          <div className="phc-matched-list">
            {groups.matched.map((r) => {
              const val = effectiveEditingAmounts[r.positionRef] !== undefined
                ? effectiveEditingAmounts[r.positionRef]
                : (r.currentValue || '');
              const isHybrid = r.category && /hybrid/i.test(r.category);

              return (
                <div key={r.positionRef} className="phc-matched-row">
                  <div className="phc-matched-info">
                    <strong className="phc-matched-title">{r.matchedName || r.originalName}</strong>
                    <p className="phc-matched-source">
                      In file: “{r.originalName}”{isHybrid ? ' · hybrid: company share portion compared' : ''}
                    </p>
                  </div>

                  <div className="phc-matched-controls">
                    <div className="phc-amount-field-wrapper">
                      <span className="phc-amount-symbol">₹</span>
                      <input
                        type="text"
                        className="phc-amount-editable-input"
                        value={val}
                        disabled={busy || isClient}
                        readOnly={isClient}
                        style={isClient ? { cursor: 'default', background: '#faf8ef' } : undefined}
                        onChange={(e) => handleAmountChange(r.positionRef, e.target.value)}
                        onBlur={() => handleAmountBlur(r)}
                        onKeyDown={(e) => handleAmountKeyDown(e, r)}
                        aria-label={`Current amount for ${r.matchedName || r.originalName}`}
                      />
                    </div>
                    {!isClient && (
                      <>
                        <button
                          type="button"
                          className="phc-link"
                          disabled={busy}
                          onClick={() => toggleChanging(r.positionRef)}
                        >
                          Change
                        </button>
                        <button
                          type="button"
                          className="phc-link"
                          disabled={busy}
                          onClick={() => onEdit([{ action: 'leave_out', positionRef: r.positionRef }])}
                        >
                          Leave out
                        </button>
                      </>
                    )}
                  </div>

                  {effectiveChanging === r.positionRef && (
                    <div className="phc-changing-container">
                      <FundCandidateSelector
                        row={r}
                        onSearch={onSearch}
                        busy={busy}
                        isClient={isClient}
                        onCancel={() => toggleChanging(null)}
                        onChoose={async (token) => {
                          const ok = await onEdit([{ action: 'change', positionRef: r.positionRef, candidateToken: token }]);
                          if (ok) toggleChanging(null);
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </details>
    </div>
  );
}
